import { Auth0Client, type CacheLocation } from '@auth0/auth0-spa-js';
import { datadogRum } from '@datadog/browser-rum';

import {
  GroupType,
  LoginTargetAction,
  ReferringFeature,
  UserLoginInfo,
} from '@/helpers/Authentication/types';
import { logError } from '@/helpers/LogHelper';

import { isPlatformHg } from '../platformDetection';
import { RedirectAction, RedirectState } from './redirect';

const auth0 =
  typeof window !== 'undefined' &&
  window.crypto?.subtle &&
  new Auth0Client({
    domain: import.meta.env.VUE_AUTH0_DOMAIN,
    clientId: import.meta.env.VUE_AUTH0_CLIENT_ID,
    authorizationParams: {
      audience: import.meta.env.VUE_AUTH0_AUDIENCE,
      redirect_uri: import.meta.env.VUE_AUTH0_REDIRECT_URL,
    },
    cacheLocation: import.meta.env.VUE_AUTH0_CACHE_LOCATION as CacheLocation | undefined,
    cookieDomain: import.meta.env.VUE_AUTH0_COOKIE_DOMAIN,
    useRefreshTokens: true,
    useRefreshTokensFallback: true,
    sessionCheckExpiryDays: 365,
  });

export const isUserLoggedIn = async (): Promise<boolean> => {
  if (!auth0) {
    return false;
  }

  try {
    await fillCache();
    return await auth0.isAuthenticated();
  } catch (error) {
    throwUnexpectedError(error); // not logged-in
    return false;
  }
};

export const getAuthToken = async (): Promise<string | undefined> => {
  if (!auth0) {
    return undefined;
  }

  if (!(await isUserLoggedIn())) {
    return undefined;
  }

  try {
    return await auth0.getTokenSilently();
  } catch (err) {
    throwUnexpectedError(err); // not logged-in
    return undefined;
  }
};

const throwUnexpectedError = (err: any): void => {
  const isExpectedError = ['missing_refresh_token', 'login_required'].some(
    (expectedError) => err.error == expectedError,
  );

  if (!isExpectedError) {
    logError('Unexpected error getting auth token', err);
    throw err; // this would block further execution for unexpected errors
  }
};

/*
 * HG uses the runtime memory of the application so if the user navigates to a route in a different project the cache is lost.
 * That's why we need to call checkSession
 */
const fillCache = async (): Promise<void> => {
  if (!auth0) return;

  const isValidEnvironment = ['dev', 'test', 'prod'].includes(import.meta.env.VUE_STAGE);

  try {
    await (isValidEnvironment ? auth0.checkSession() : auth0.getTokenSilently());
  } catch (err) {
    throwUnexpectedError(err);
  }
};

export async function getUserLoginInfo(): Promise<UserLoginInfo | undefined> {
  if (!auth0) {
    return undefined;
  }

  // This will fill the cache with the user information if the user is logged in so that getUser can retrieve it
  await fillCache();

  try {
    const user = await auth0.getUser();

    if (user) {
      return {
        username: user.preferred_username || '',
        email: user.email,
        name: user.name,
        picture: user.picture,
        groups: user['cognito:groups'] || [],
        userId: user['cognito:username'],
        loginProvider: user.sub?.split('|')[0],
      };
    }

    return undefined;
  } catch (error) {
    throwUnexpectedError(error);
    return undefined; // not logged-in
  }
}

export async function isPrivateLoggedInUser(): Promise<boolean> {
  try {
    const user = await getUserLoginInfo();

    return user?.groups?.includes(GroupType.PRIVATE) || false;
  } catch (err) {
    throwUnexpectedError(err);
    return false; // not logged-in
  }
}

export async function loginWithRedirect<T extends RedirectAction>(
  auth0ScreenHint: LoginTargetAction,
  redirectAction: T,
  redirectPayload: Extract<RedirectState, { redirectAction: T }>['redirectPayload'],
  referringFeature?: ReferringFeature,
  loginParams?: {
    login_hint: string;
    connection: string;
  },
): Promise<void> {
  if (!auth0) {
    throw new Error('Auth0 is not initialized');
  }

  const redirectState = {
    redirectUrl: window.location.href,
    redirectAction,
    redirectPayload,
  };

  await auth0.loginWithRedirect({
    authorizationParams: {
      redirect_uri: import.meta.env.VUE_AUTH0_SEEKER_REDIRECT_URL,
      screen_hint: auth0ScreenHint,
      referral_url: window.location.href,
      referring_feature: referringFeature || '',
      referring_platform: isPlatformHg() ? 'homegate_web' : 'immoscout24_web',
      ...loginParams,
    },
    // Avoid redirecting to login-callback page, which would cause an infinite redirection loop
    appState: redirectState.redirectUrl.includes('login-callback') ? undefined : redirectState,
  });
}

export async function retrieveAppState(): Promise<RedirectState | undefined> {
  if (!auth0) {
    throw new Error('Auth0 is not initialized');
  }

  try {
    return (await auth0.handleRedirectCallback()).appState as RedirectState;
  } catch (err) {
    datadogRum.addError(err);
    throw new Error('Error retrieving app state');
  }
}
